import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import AuthContext from '../../components/shared/AuthContext';
import PageMeta from '../../components/seo/PageMeta';
import AdaptivePageShell from '../../features/adaptive-learning/AdaptivePageShell';
import { SKILL_GAP_BUCKETS } from '../../features/adaptive-learning/adaptiveUtils';

export default function SkillGapPage() {
  const { apiClient, userProfile } = useContext(AuthContext);
  const [jobTitle, setJobTitle] = useState('Senior Backend Engineer');
  const [jd, setJd] = useState('');
  const [result, setResult] = useState(null);
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const analyze = async () => {
    if (jd.trim().length < 40) {
      setError('Paste a fuller job description (at least a few lines).');
      return;
    }
    setBusy(true);
    setError('');
    setPlan(null);
    try {
      const res = await apiClient.post('/api/v1/skill-gap/analyze', {
        jobTitle,
        jobDescription: jd,
      });
      setResult(res.data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Analysis failed. Check AI quota.');
    } finally {
      setBusy(false);
    }
  };

  const createPlan = async () => {
    if (!result?.analysisId) return;
    setBusy(true);
    setError('');
    try {
      const res = await apiClient.post('/api/v1/learning-plans', {
        analysisId: result.analysisId,
        days: 30,
      });
      setPlan(res.data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not create plan.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageMeta title="Skill gap" description="Compare a job description to your learning profile." />
      <AdaptivePageShell userProfile={userProfile}>
        <header className="adaptive-hero">
          <h1>Skill gap analysis</h1>
          <p className="adaptive-disclaimer">Skill coverage estimate — not a hiring prediction.</p>
        </header>

        {error && <p className="adaptive-error" role="alert">{error}</p>}

        <div className="adaptive-card">
          <label className="adaptive-label">
            Role title
            <input className="learn-input" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} />
          </label>
          <label className="adaptive-label">
            Job description
            <textarea
              className="learn-input import-textarea"
              rows={10}
              value={jd}
              onChange={(e) => setJd(e.target.value)}
              placeholder="Paste requirements, responsibilities, and tech stack…"
            />
          </label>
          <button type="button" className="learn-btn learn-btn--primary" disabled={busy} onClick={analyze}>
            {busy ? 'Analyzing…' : 'Analyze skills'}
          </button>
        </div>

        {result && (
          <>
            <div className="skill-gap-grid">
              {Object.entries(SKILL_GAP_BUCKETS).map(([key, meta]) => (
                <section key={key} className={`skill-gap-bucket skill-gap-bucket--${key}`}>
                  <h3>{meta.title}</h3>
                  <p className="adaptive-muted">{meta.desc}</p>
                  <ul>
                    {(result[key] || []).length === 0 && <li>—</li>}
                    {(result[key] || []).map((s) => (
                      <li key={s}>{s}</li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
            <div className="adaptive-start-row">
              <button type="button" className="learn-btn learn-btn--primary" disabled={busy} onClick={createPlan}>
                Create 30-day learning plan
              </button>
              <Link to="/hub/today" className="learn-btn learn-btn-secondary">
                Open today&apos;s plan
              </Link>
            </div>
          </>
        )}

        {plan && (
          <section className="adaptive-card">
            <h2>{plan.title}</h2>
            <p className="adaptive-muted">Plan saved — use Today&apos;s plan and notebooks to execute.</p>
            {typeof plan.plan === 'object' && plan.plan?.weeks ? (
              <ul>
                {plan.plan.weeks.map((w, i) => (
                  <li key={i}>
                    <strong>{w.title || `Week ${i + 1}`}</strong>
                    {w.topics?.length ? `: ${w.topics.join(', ')}` : ''}
                  </li>
                ))}
              </ul>
            ) : (
              <pre className="adaptive-json">{JSON.stringify(plan.plan, null, 2)}</pre>
            )}
          </section>
        )}
      </AdaptivePageShell>
    </>
  );
}
